import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    }
  }
  const p = new Date(d);
  if (isNaN(p)) return '-';
  return `${String(p.getDate()).padStart(2,'0')}/${String(p.getMonth()+1).padStart(2,'0')}/${p.getFullYear()}`;
};

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const ageBucket = (days) => {
  if (days <= 30)  return { bg: '#dcfce7', color: '#166534', label: '0-30' };
  if (days <= 60)  return { bg: '#fef9c3', color: '#854d0e', label: '31-60' };
  if (days <= 90)  return { bg: '#fed7aa', color: '#9a3412', label: '61-90' };
  return               { bg: '#fee2e2', color: '#991b1b', label: '90+' };
};

const ClientAgeingAnalysisBillWise = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [fetched, setFetched]     = useState(false);
  const [clients, setClients]     = useState([]);
  const [masterId, setMasterId]   = useState('');
  const [search, setSearch]       = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token)
        .then(res => setClients(res?.response_data || res || []))
        .catch(() => setClients([]));
    }
  }, [companyId, token]);

  const fetchReport = async () => {
    if (!masterId) { setError('Please select a client'); return; }
    setLoading(true);
    try {
      const res = await fetchData('SaleReportApi', `/ClientBillToBillAgeing/${companyId}/${branchId}/${masterId}`, token);
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch client ageing report');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.BillNo || '').toLowerCase().includes(q) ||
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.ContactNumber || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Bucket summary
  const bucketSummary = useMemo(() => {
    const b = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    filtered.forEach(r => { b[ageBucket(r.BillAgeDays || 0).label] += parseFloat(r.BillAmount) || 0; });
    return b;
  }, [filtered]);

  const grandTotal = useMemo(() =>
    filtered.reduce((s, r) => s + (parseFloat(r.BillAmount) || 0), 0), [filtered]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'ClientAgeingBillWise', 0);
    if (!filtered.length) return;
    const clientName = clients.find(c => String(c.MasterId || c.Id) === String(masterId))?.Name || '';
    const headers = ['#', 'Bill No', 'Bill Date', 'Due Date',
      'Customer', 'Contact', 'Bill Amount', 'Bill Age Days', 'Bucket'];
    const rows = filtered.map((r, i) => [
      i + 1, r.BillNo, fmtDate(r.BillDate), fmtDate(r.DueDate),
      r.CustomerName, r.ContactNumber,
      r.BillAmount, r.BillAgeDays, ageBucket(r.BillAgeDays || 0).label,
    ]);

      await exportStyledExcel({
          reportTitle: 'CLIENT AGEING ANALYSIS - BILL TO BILL',
          sheetName: 'Ageing Bill Wise',
          fileName: `ClientAgeingBillWise_${clientName}.xlsx`,
          companyName,
          username,
          headers: headers,
          rows,
          totalsRow: ['', '', '', '', '', '', 'Total', fmtN(grandTotal), '', ''],
          columnWidths: [4, 8, 14, 12, 12, 24, 14, 14, 12, 10],
      });
  };

  const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #9fa8da', color: '#fff', background: PRIMARY_MID };
  const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading ageing report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Client Ageing Analysis
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Bill to Bill</span>
          </span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input
            placeholder="Search bill / customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 160, outline: 'none' }}
          />
          <SearchableDropdown
            value={masterId}
            onChange={v => setMasterId(v)}
            options={clients.map(c => ({ value: String(c.MasterId || c.Id), label: c.Name }))}
            placeholder="— Select Client —"
            minWidth={180}
            primaryColor={PRIMARY}
          />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Ageing bucket summary bar */}
        {fetched && filtered.length > 0 && (
          <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
            {[
              { label: '0-30 Days', key: '0-30', bg: '#dcfce7', color: '#166534' },
              { label: '31-60 Days', key: '31-60', bg: '#fef9c3', color: '#854d0e' },
              { label: '61-90 Days', key: '61-90', bg: '#fed7aa', color: '#9a3412' },
              { label: '90+ Days',  key: '90+',   bg: '#fee2e2', color: '#991b1b' },
            ].map(b => (
              <div key={b.key} style={{ background: b.bg, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                <span style={{ fontSize: 10, color: b.color, fontWeight: 600 }}>{b.label}</span>
                <span style={{ fontSize: 12, color: b.color, fontWeight: 700 }}>{fmtN(bucketSummary[b.key])}</span>
              </div>
            ))}
            <div style={{ background: '#e8eaf6', borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
              <span style={{ fontSize: 10, color: PRIMARY, fontWeight: 600 }}>Total</span>
              <span style={{ fontSize: 12, color: PRIMARY, fontWeight: 700 }}>{fmtN(grandTotal)}</span>
            </div>
          </div>
        )}

        {/* Meta */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
            {filtered.length} bills{search ? ` (filtered from ${data.length})` : ''}
            &nbsp;|&nbsp; Total: <strong style={{ color: PRIMARY }}>{fmtN(grandTotal)}</strong>
          </div>
        )}

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 180px)' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 400, width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                {/* <th style={{ ...thBase, textAlign: 'center' }}>Sale ID</th> */}
                <th style={{ ...thBase, textAlign: 'left' }}>Bill No</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Due Date</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 180 }}>Customer</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Contact</th>
                <th style={{ ...thBase, textAlign: 'right', background: '#0f4c81' }}>Bill Amount</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Age Days</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bucket</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select a client and click Refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const bucket = ageBucket(r.BillAgeDays || 0);
                  const rowBg  = i % 2 === 0 ? '#fff' : '#e8eaf6';
                  return (
                    <tr key={`${r.SaleId}-${i}`} style={{ background: rowBg }}>
                      <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{r.RN || i + 1}</td>
                      {/* <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: PRIMARY }}>{r.SaleId}</td> */}
                      <td style={{ ...tdBase, fontWeight: 500, color: PRIMARY }}>{r.BillNo || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: (r.BillAgeDays || 0) > 30 ? '#dc2626' : '#374151', fontWeight: (r.BillAgeDays || 0) > 30 ? 600 : 400 }}>
                        {fmtDate(r.DueDate)}
                      </td>
                      <td style={{ ...tdBase, fontWeight: 500 }}>{r.CustomerName}</td>
                      <td style={{ ...tdBase, color: '#0277bd' }}>{r.ContactNumber || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY, background: '#f0f4ff' }}>{fmtN(r.BillAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: bucket.bg, color: bucket.color }}>
                          {r.BillAgeDays ?? '-'}
                        </span>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: bucket.bg, color: bucket.color }}>
                          {bucket.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: '#e8eaf6', fontWeight: 700 }}>
                  <td colSpan={6} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Total</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY, background: '#bfdbfe' }}>{fmtN(grandTotal)}</td>
                  <td colSpan={2} style={{ ...tdBase }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientAgeingAnalysisBillWise;




